"use client";

import React from "react";
import { PhoneCall, Clock, ShieldCheck, Wrench, CircleCheck as CheckCircle2, MapPin, ArrowRight, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const keyHighlights = [{
  text: "Local Morrow Technicians",
  icon: MapPin
}, {
  text: "Upfront Transparent Pricing",
  icon: CheckCircle2
}, {
  text: "Same-Day & Emergency Dispatch",
  icon: Zap
}, {
  text: "Fully Licensed & Insured",
  icon: ShieldCheck
}];
export default function MorrowHero() {
  const prefersReducedMotion = useReducedMotion();
  const animationProps = prefersReducedMotion ? {
    initial: {
      opacity: 1
    },
    animate: {
      opacity: 1
    }
  } : {
    initial: {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5
    }
  };
  return <section id="morrowhero" data-nav="light" className="relative bg-background text-foreground overflow-hidden py-12 md:py-20 lg:py-24 border-b border-border">
      {/* Background Decorative Gradients */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-24 -right-24 size-96 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute top-1/2 -left-24 size-96 rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* Left Column: Core Copy & CTAs */}
          <motion.div className="lg:col-span-7 flex flex-col gap-6" {...animationProps}>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="bg-primary/5 border-primary/20 text-primary px-3 py-1 text-xs font-semibold rounded-full flex items-center gap-1.5">
                <MapPin className="size-3.5 text-accent" />
                <span>Serving Morrow, GA &amp; Surrounding Areas</span>
              </Badge>
              <Badge variant="secondary" className="bg-accent text-accent-foreground font-semibold px-3 py-1 text-xs rounded-full flex items-center gap-1.5">
                <Clock className="size-3.5 text-accent-foreground" />
                <span>24/7 Immediate Response</span>
              </Badge>
            </div>

            <div className="flex flex-col gap-3">
              <h1 className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-primary leading-[1.15]">
                Plumbing Services in <span className="text-foreground">Morrow, GA</span>
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl font-medium text-muted-foreground max-w-2xl">
                Fast Plumbing Service Available 24/7 in Morrow
              </p>
            </div>

            <p className="text-base text-foreground/80 max-w-2xl leading-relaxed">
              When plumbing issues threaten your home or commercial property, our certified local specialists arrive promptly with fully equipped trucks to resolve leaks, clogged drains, water heater failures, and urgent sewer line problems.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold text-base h-12 px-7">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <span>Request Service</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-2 border-accent bg-background text-foreground hover:bg-accent/10 shadow-sm font-semibold text-base h-12 px-7">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                  <PhoneCall className="size-4 text-accent" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
            </div>

            {/* Quick Value Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t border-border">
              {keyHighlights.map((item, index) => {
              const IconComponent = item.icon;
              return <div key={index} data-index={index} className="flex items-center gap-2.5">
                    <div className="size-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <IconComponent className="size-4 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{item.text}</span>
                  </div>;
            })}
            </div>
          </motion.div>

          {/* Right Column: Hero Visual & Trust Card */}
          <motion.div className="lg:col-span-5 relative" initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }}>
            <div className="relative mx-auto max-w-md lg:max-w-none">
              
              {/* Primary Image Container */}
              <div className="relative rounded-2xl overflow-hidden border-4 border-card shadow-xl bg-muted aspect-[4/3] sm:aspect-[4/3] lg:aspect-[5/4]">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_repairing_pipes_in_morrow.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_repairing_pipes_in_morrow.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_repairing_pipes_in_morrow.png-wvc-sizes" alt="Professional plumber providing pipe repair services in Morrow GA" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                
                {/* Image Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent pointer-events-none" />

                {/* Overlay Text Inside Image */}
                <div className="absolute bottom-4 left-4 right-4 text-primary-foreground pointer-events-none">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="size-2 rounded-full bg-accent animate-pulse" />
                    <span className="text-xs uppercase tracking-wider font-semibold text-accent">Active Dispatch</span>
                  </div>
                  <p className="text-sm font-medium">Jonesboro Emergency Plumber in Morrow, GA</p>
                </div>
              </div>

              {/* Floating Emergency Badge Card */}
              <Card className="absolute -bottom-6 -left-4 sm:-left-6 max-w-[260px] bg-card text-card-foreground border border-border shadow-lg p-3 sm:p-4 rounded-xl">
                <CardContent className="p-0 flex items-center gap-3">
                  <div className="size-11 rounded-lg bg-accent/20 flex items-center justify-center shrink-0">
                    <Wrench className="size-6 text-accent" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Fast Arrival</span>
                    <span className="text-sm font-bold text-foreground">60-Min Target Dispatch</span>
                  </div>
                </CardContent>
              </Card>

              {/* Floating Rating Pill */}
              <div className="absolute -top-4 -right-2 sm:-right-4 bg-primary text-primary-foreground border-2 border-background shadow-md px-3.5 py-1.5 rounded-full flex items-center gap-1.5 text-xs font-semibold">
                <ShieldCheck className="size-4 text-accent" />
                <span>100% Satisfaction Guarantee</span>
              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}